import "./../theme/init.scss";

/**
 * This script tracks user visits and sends the data to a backend endpoint.
 * It collects information about the URL, referrer, campaign parameters, and device.
 */

// Helper function to generate a UUID (v4)
const generateUUID = () => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
        const r = Math.random() * 16 | 0,
            v = c == 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
    });
};

// Helper function to get a cookie value by name
const getCookie = (name) => {
    const nameEQ = name + "=";
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
        let c = ca[i];
        while (c.charAt(0) === ' ') c = c.substring(1, c.length);
        if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
    }
    return null;
};

// Helper function to set a cookie with a given name, value, and expiration in days
const setCookie = (name, value, days) => {
    let expires = "";
    if (days) {
        const date = new Date();
        date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
        expires = "; expires=" + date.toUTCString();
    }
    document.cookie = name + "=" + (value || "") + expires + "; path=/; SameSite=Lax";
};

const initTracking = () => {
    // Send the main pageview hit immediately on page load
    document.addEventListener('DOMContentLoaded', () => {
        const trackingData = gatherTrackingData();
        sendTrackingData(trackingData);
    });

    // Send a lightweight heartbeat hit when the user leaves the page
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') {
            const visitorUuid = getCookie('_tv');
            if (visitorUuid) {
                sendTrackingData({
                    visitor_uuid: visitorUuid,
                    type: 'heartbeat'
                });
            }
        }
    });
};

const gatherTrackingData = () => {
    let visitorUuid = getCookie('_tv');
    if (!visitorUuid) {
        visitorUuid = generateUUID();
        setCookie('_tv', visitorUuid, 730);
    }

    const urlParams = new URLSearchParams(window.location.search);

    const campaign = {
        source: urlParams.get('utm_source'),
        medium: urlParams.get('utm_medium'),
        campaign: urlParams.get('utm_campaign'),
        term: urlParams.get('utm_term'),
        content: urlParams.get('utm_content'),
    };

    const hasCampaignData = Object.values(campaign).some(v => v !== null);

    const referrerInfo = getReferrerInfo(document.referrer);
    const deviceInfo = getDeviceInfo(navigator.userAgent);

    const data = {
        type: 'pageview', // Distinguish this as a full pageview hit
		title: document.title,
        visitor_uuid: visitorUuid,
        url: window.location.href,
        referrer: document.referrer || null,
        referrer_type: referrerInfo.referrer_type,
        source: referrerInfo.source,
        device_type: deviceInfo.device_type,
        os: deviceInfo.os,
    };

    if (hasCampaignData) {
        data.campaign = campaign;
    }

    return data;
};

const getReferrerInfo = (referrer) => {
    if (!referrer) {
        return { referrer_type: 'direct', source: null };
    }

    try {
        const referrerUrl = new URL(referrer);
        const referrerHost = referrerUrl.hostname.replace(/^www\./, '');
        const currentHost = window.location.hostname.replace(/^www\./, '');

        if (referrerHost === currentHost) {
            return { referrer_type: 'internal', source: null };
        }

        const socialNetworks = {
            'facebook.com': 'Facebook', 't.co': 'Twitter', 'twitter.com': 'Twitter',
            'linkedin.com': 'LinkedIn', 'instagram.com': 'Instagram', 'pinterest.com': 'Pinterest',
            'reddit.com': 'Reddit', 'youtube.com': 'YouTube'
        };
        for (const domain in socialNetworks) {
            if (referrerHost.includes(domain)) {
                return { referrer_type: 'social', source: socialNetworks[domain] };
            }
        }

        const searchEngines = {
            'google.': 'Google', 'bing.com': 'Bing', 'yahoo.com': 'Yahoo',
            'duckduckgo.com': 'DuckDuckGo', 'seznam.cz': 'Seznam', 'baidu.com': 'Baidu', 'yandex.ru': 'Yandex'
        };
        for (const domain in searchEngines) {
            if (referrerHost.includes(domain)) {
                return { referrer_type: 'search', source: searchEngines[domain] };
            }
        }

        return { referrer_type: 'referral', source: referrerHost };

    } catch (e) {
        return { referrer_type: 'unknown', source: referrer };
    }
};

const getDeviceInfo = (userAgent) => {
    let device_type = 'desktop';
    let os = 'unknown';

    if (/Android/i.test(userAgent)) {
        os = 'android';
        device_type = /mobile/i.test(userAgent) ? 'mobile' : 'tablet';
    } else if (/iPhone|iPod/.test(userAgent)) {
        os = 'ios';
        device_type = 'mobile';
    } else if (/iPad/.test(userAgent)) {
        os = 'ios';
        device_type = 'tablet';
    } else if (/(tablet|playbook|silk)|(android(?!.*mobi))/i.test(userAgent)) {
        device_type = "tablet";
    }

    if (os === 'unknown') {
        if (/Windows/i.test(userAgent)) os = 'windows';
        else if (/Macintosh|Mac OS X/i.test(userAgent)) os = 'macos';
        else if (/Linux/i.test(userAgent)) os = 'linux';
    }

    return { device_type, os };
};

const sendTrackingData = (data) => {
	const endpoint = '/tracking/hit';
	const isDebug = typeof Tracy !== 'undefined' && Tracy !== null;

	if (isDebug) {
		const queryString = new URLSearchParams(data).toString();
		const finalUrl = `${endpoint}?${queryString}`;

		fetch(finalUrl, {
			keepalive: true
		})
			.then(response => {
				if (!response.ok) {
					console.error('Tracking fetch failed. Status:', response.status);
				} else {
					console.log('Tracking (GET Debug) sent successfully.');
				}
			})
			.catch(error => console.error('Tracking fetch error:', error));

	} else if (navigator.sendBeacon) {
		const blob = new Blob([JSON.stringify(data)], { type: 'application/json; charset=UTF-8' });
		navigator.sendBeacon(endpoint, blob);
	}
};

initTracking();
